/**
 * Which user directory an actor id belongs to.
 *
 * cpaas-api is reachable through two front doors backed by separate user tables,
 * so a user id on its own is ambiguous — portal user 100 and cpaas user 100 are
 * different people:
 *
 *   PORTAL — `portal_users`, arriving through portal-api's proxy
 *   CPAAS  — `sidewinder.users`, arriving from cpaas-sidecar's own session
 *   SYSTEM — automation with no user at all (webhooks, migrations, generated docs)
 *
 * This lib is browser-shared — the Angular apps import it — so it holds the types
 * only. Encoding, decoding and deriving an actor are node-side concerns and live in
 * `@nuso/backend/util-http`.
 */
export enum ActorSource {
  PORTAL = 'PORTAL',
  CPAAS = 'CPAAS',
  SYSTEM = 'SYSTEM',
}

/**
 * Which side of the porting conversation an actor is on: Nuso staff, or the
 * customer (a reseller or one of its accounts). Porting notes are read per side
 * rather than per person, so a note one Nuso admin has read is read for all of them.
 *
 * Distinct from ActorSource: a Nuso admin and a customer can both arrive through
 * either front door.
 */
export enum ActorSide {
  NUSO = 'NUSO',
  CUSTOMER = 'CUSTOMER',
}

export interface RequestActor {
  source: ActorSource;
  // null for SYSTEM: automation speaks for neither side.
  side: ActorSide | null;
  userId: number | null;
  userName: string | null;
  userEmail: string | null;
}

/**
 * The forwarded identity, without a source. The receiver derives the source from
 * which authentication branch the request arrived on — a sender able to assert its
 * own source would defeat the point of deriving it.
 */
export type ForwardedActor = Omit<RequestActor, 'source'>;
